import React, { useEffect, useState } from "react";

import { Grid, MenuItem } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import BadgeJobStatus from "Components/Generic/BadgeJobStatus";
import { useTranslation } from "react-i18next";

import { Select } from "../../../../../../../../../../../../components/Form";
import AutoSuggest from "../../../../../../../../../../../../components/Form/AutoSuggest";
import { LoadingSpinner } from "../../../../../../../../../../../../components/LoadingModal/styles";
import { useAppState } from "../../../../../../../../../../../../context/StateContext";
import useYupValidation from "../../../../../../../../../../../../hooks/useYupValidation";
import { Job } from "../../../../../../../../../../../../models/job";
import { Searches } from "../../../../../../../../../../../../models/shipeezi.table";
import OrderService from "../../../../../../../../../../../../services/order.service";
import { OrderKeys } from "../../../../../../../../../../../../util/queries";
import { useActions } from "../../../../../hooks/actions";
import { TimesheetForm } from "../TimesheetForm";
import { EmptyState } from "./EmptyState";
import useStyles, {
  AutoSuggestField,
  AutoSuggestLabel,
  AutoSuggestSelect,
  AutoSuggestWrapper,
  CustomerName,
  OrderOptionHeader,
  OrderReference,
  TaskAddress,
  TaskAddressContainer,
  TimesheetWrapper,
  OrderOption,
  Arrow,
} from "./styles";

type ExistentTaskForTimesheetProps = {
  isLoading: boolean;
  isOnView?: boolean;
};

enum SearchOptions {
  REFERENCE = "reference",
  SECOND_REFERENCE = "secondReference",
  CUSTOMER = "customer",
  MOVE_COORDINATOR = "moveCoordinator",
  ORIGINS = "origins",
  DESTINATIONS = "destinations",
  TAG = "tag",
}

export const ExistentTaskForTimesheet: React.FC<ExistentTaskForTimesheetProps> = (props) => {
  const { isOnView = false, isLoading } = props;
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const { timesheets, setTimesheets, timesheetErrors } = useActions();
  const { fieldHasError, getErrorByField } = useYupValidation(timesheetErrors);
  const classes = useStyles();

  const [searchFilter, setSearchFilter] = useState<string | null>(null);
  const [search, setSearch] = useState<Searches>([]);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [order, setOrder] = useState<Job | null>(null);

  const filters = [
    {
      searchFor: SearchOptions.REFERENCE,
      label: translate("reference"),
    },
    {
      searchFor: SearchOptions.SECOND_REFERENCE,
      label: translate("secondReference"),
    },
    {
      searchFor: SearchOptions.CUSTOMER,
      label: translate("customer"),
    },
    {
      searchFor: SearchOptions.MOVE_COORDINATOR,
      label: translate("moveCoordinator"),
    },
    {
      searchFor: SearchOptions.ORIGINS,
      label: translate("origins"),
    },
    {
      searchFor: SearchOptions.DESTINATIONS,
      label: translate("destinations"),
    },
    {
      searchFor: SearchOptions.TAG,
      label: translate("tag"),
    },
  ];

  const { data: orderList, isFetching } = useQuery({
    queryKey: OrderKeys.getAll([search]),
    queryFn: async () => {
      const response = await OrderService.getAll({
        companyId: currentUser?.companyId,
        userId: currentUser?.id,
        searchByOr: search?.length ? JSON.stringify(search) : "",
        include: JSON.stringify([
          {
            association: "tasks",
          },
        ]),
      });

      if (response?.data) return response?.data as Job[];

      return [];
    },
    enabled: !!search?.length && !timesheets?.id,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (searchValue && searchValue.length > 0) {
      setSearch([
        {
          [searchFilter as string]: searchValue,
        },
      ]);
    } else {
      setSearch([]);
    }
  }, [searchValue]);

  useEffect(() => {
    if (isOnView) {
      setSearchFilter("reference");
      setSearchValue(timesheets?.task?.job?.reference || "");
    } else if (timesheets?.searchBy && timesheets?.searchByValue && timesheets?.id) {
      setSearchFilter(timesheets?.searchBy);
      setSearchValue(timesheets?.searchByValue);
    }
  }, [timesheets]);

  useEffect(() => {
    setSearchFilter(SearchOptions.REFERENCE);
    setTimesheets({
      ...(timesheets || {}),
      searchBy: SearchOptions.REFERENCE,
    });
  }, []);

  return (
    <div>
      <Grid container xs={12} direction="row" spacing={2} alignItems="flex-end">
        <Grid item xs={12} md={8}>
          <AutoSuggestWrapper>
            <AutoSuggestLabel>{translate("searchOrderBy")}</AutoSuggestLabel>
            <div>
              <AutoSuggestSelect
                value={searchFilter || ""}
                disabled={!!timesheets?.id || isOnView}
                onChange={(event: any) => {
                  const { value } = event.target;

                  setTimesheets({
                    ...(timesheets || {}),
                    searchBy: value,
                  });
                  setSearchFilter(value);
                }}
              >
                {filters?.map((filter) => (
                  <MenuItem key={filter.searchFor} value={filter.searchFor}>
                    {filter.label}
                  </MenuItem>
                ))}
              </AutoSuggestSelect>
              <AutoSuggestField>
                <AutoSuggest
                  disabled={!!timesheets?.id || isOnView}
                  placeholder={translate("search")}
                  inputValue={searchValue || ""}
                  getOptionLabel={(option: Job) => option?.reference || ""}
                  getOptionSelected={(option: Job, value: Job) => option?.id === value?.id}
                  options={orderList || []}
                  filterOptions={(options) => options}
                  onChange={(option: Job) => {
                    setOrder(option);
                  }}
                  onChangeInput={(value) => {
                    setTimesheets({
                      ...(timesheets || {}),
                      searchByValue: value,
                    });
                    setSearchValue(value);
                  }}
                  renderOption={(props, option) => {
                    const job = option as Job;

                    return (
                      <div {...props}>
                        <OrderOption>
                          <OrderOptionHeader>
                            <OrderReference>#{job?.reference}</OrderReference>
                            <BadgeJobStatus value={job?.status} className={classes.statusChip} />
                          </OrderOptionHeader>
                          <div>
                            <CustomerName>{job?.customer?.fullName || ""}</CustomerName>
                            <TaskAddressContainer>
                              <TaskAddress>{`${
                                job?.origins?.[0]?.state && job?.origins?.[0]?.state !== undefined
                                  ? `${job?.origins?.[0]?.state}, `
                                  : `${job?.origins?.[0]?.longAddress}, `
                              }${job?.origins?.[0]?.country}`}</TaskAddress>
                              <Arrow>→</Arrow>
                              <TaskAddress>{`${
                                job?.destinations?.[0]?.state &&
                                job?.destinations?.[0]?.state !== undefined
                                  ? `${job?.destinations?.[0]?.state}, `
                                  : `${job?.destinations?.[0]?.longAddress}, `
                              }${job?.destinations?.[0]?.country}`}</TaskAddress>
                            </TaskAddressContainer>
                          </div>
                        </OrderOption>
                      </div>
                    );
                  }}
                />
                {isFetching && <LoadingSpinner size={20} />}
              </AutoSuggestField>
            </div>
          </AutoSuggestWrapper>
        </Grid>
        <Grid item xs={12} md={4}>
          {order && order?.tasks?.length && (
            <Select
              label={translate("task")}
              value={timesheets?.taskId}
              onChange={(event) =>
                setTimesheets({ ...(timesheets || {}), taskId: Number(event.target.value) })
              }
              error={fieldHasError("taskId")}
              errorMessage={getErrorByField("taskId")}
              disabled={!!timesheets?.id || isOnView || isLoading}
            >
              {order?.tasks?.length &&
                order?.tasks?.map((task) => (
                  <MenuItem key={task.id} value={task.id}>
                    {task?.type}
                  </MenuItem>
                ))}
              {order?.tasks?.length === 0 && <MenuItem value="">{translate("noTasks")}</MenuItem>}
            </Select>
          )}
        </Grid>
      </Grid>

      <TimesheetWrapper>
        {timesheets?.taskId ? (
          <TimesheetForm isOnView={isOnView} isLoading={isLoading} />
        ) : (
          <EmptyState />
        )}
      </TimesheetWrapper>
    </div>
  );
};
